import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import Hint from '../../../../../components/hint';
import StringToHtml from '../../../../../components/StringToHtml';
import { Theme } from '../../../../../theme';
import { NotRequiredFeedbackAnswer } from '../../EmployeeFeedback/FeedbackAnswer';
import { EmployeeFeedbackInput } from '../EmployeeFeedbackInput';
import { IEmployeeFeedbackAnswer } from '../types';

type Props = {
  answer: IEmployeeFeedbackAnswer;
  onUpdateAnswer: (
    answer: IEmployeeFeedbackAnswer,
    value: string,
    notes: string,
  ) => void;
  readOnly: boolean;
};

const FieldContainer = styled.div({
  marginBottom: '2.25rem',
});

const HintSpan = styled.span({
  color: Theme.color.gray2,
});

export const EmployeeFeedbackField: React.FC<
  React.PropsWithChildren<Props>
> = ({ answer, onUpdateAnswer, readOnly }) => {
  const [value, setValue] = useState<string>(answer.answer ?? '');
  const [notes, setNotes] = useState<string>(answer.notes ?? '');

  const onChange = useCallback(
    (value: string) => {
      setValue(value);
      onUpdateAnswer(answer, value, notes);
    },
    [setValue, answer, onUpdateAnswer, notes],
  );
  const onChangeNotes = useCallback(
    (notes: string) => {
      setNotes(notes);
      onUpdateAnswer(answer, value, notes);
    },
    [setNotes, answer, onUpdateAnswer, value],
  );

  const {
    isOptional,
    showOptionalComment,
    description,
    question,
    choices,
    requiredChoices,
    type,
    id,
    hint,
  } = answer;

  return (
    <FieldContainer>
      <Form.Label className="row align-items-center">
        <Col>
          <h3 className="mb-0">
            {!isOptional && <abbr className="text-danger me-1">*</abbr>}
            <span>{question}</span>
            {hint && (
              <HintSpan className="ms-1">
                <Hint id={`hint_${id}`} text={hint} />
              </HintSpan>
            )}
          </h3>
        </Col>
      </Form.Label>
      {description && (
        <Form.Text>
          <span>
            <StringToHtml text={description} />
          </span>
        </Form.Text>
      )}

      {!answer.showOnlyQuestionName ? (
        <EmployeeFeedbackInput
          id={answer.id}
          value={value}
          onChange={onChange}
          choices={choices}
          requiredChoices={requiredChoices}
          showOptionalComment={showOptionalComment}
          type={type}
          notes={notes}
          onChangeNotes={onChangeNotes}
          readOnly={readOnly}
        />
      ) : (
        <NotRequiredFeedbackAnswer />
      )}
    </FieldContainer>
  );
};
